/* eslint-disable @typescript-eslint/no-explicit-any */
import { ref } from "vue";
import { type $Fetch, type FetchContext, ofetch } from "ofetch";
import type {
  ExtraFetchOptions,
  IUseAuthTransport,
  IAuthorizeUserResponse,
} from "../types";
import { authRefreshTokens } from "../api";
import { DEFAULT_AUD } from "../constants";

export default (opts: IUseAuthTransport) => {
  const {
    aud,
    cookieJWT,
    refreshTokens: providedRefreshTokens,
    providedFetch,
  } = opts;
  let refreshRequest: null | Promise<IAuthorizeUserResponse> = null;

  const cookieJWTRef = ref<
    undefined | null | { access: string; refresh: string }
  >({ access: "", refresh: "" });

  const authOnResponse = async (
    ctx: FetchContext<any, any>,
    argFetch?: $Fetch,
  ) => {
    const { options, error, response } = ctx;
    const refreshToken =
      cookieJWT?.value?.refresh ?? cookieJWTRef.value?.refresh ?? "";
    if (!(options as ExtraFetchOptions).permissions?.jwt) return;
    if (response?.status === 401 && !!refreshToken) {
      try {
        const body = {
          refreshToken,
          aud: aud ?? DEFAULT_AUD,
        };
        if (!refreshRequest) {
          if (providedRefreshTokens) {
            refreshRequest = providedRefreshTokens(body);
          } else {
            const fetchFn = argFetch || providedFetch || ofetch;
            const retryTransport = fetchFn.create({
              ...options,
              retry: 0,
              onResponse: (ctx) => {
                if (
                  ctx.response?.status === 401 ||
                  ctx.response?.status === 400
                ) {
                  if (cookieJWT) cookieJWT.value = null;
                  else cookieJWTRef.value = null;
                }
              },
            });
            refreshRequest = authRefreshTokens(retryTransport, body) ?? null;
            if (!refreshRequest) throw error;
          }
        }
        const tokens = await refreshRequest;
        if (
          cookieJWT?.value?.access === tokens.accessToken &&
          cookieJWT?.value?.refresh === tokens.refreshToken
        )
          return;
        const jwtValue = {
          access: tokens.accessToken,
          refresh: tokens.refreshToken,
        };
        if (cookieJWT) cookieJWT.value = jwtValue;
        else cookieJWTRef.value = jwtValue;
      } catch {
        if (error) throw error;
      } finally {
        if (refreshRequest !== null) {
          refreshRequest = null;
        }
      }
    }
  };

  const authOnRequest = (ctx: FetchContext<any, any>) => {
    const { options } = ctx;
    const accessToken =
      cookieJWT?.value?.access ?? cookieJWTRef.value?.access ?? "";
    if (!(options as ExtraFetchOptions).permissions?.jwt || !accessToken)
      return;
    const authHeader = `Bearer ${accessToken}`;
    if (!options.headers) {
      options.headers = new Headers({ Authorization: authHeader });
    } else if (options.headers instanceof Headers) {
      options.headers.set("Authorization", authHeader);
    } else {
      (options.headers as Record<string, string>).Authorization = authHeader;
    }
    return options;
  };

  const retryOptions = { retry: 1, retryStatusCodes: [401] };

  return {
    authOnResponse,
    authOnRequest,
    retryOptions,
  };
};
